<template>
  <section class="rw-section" aria-labelledby="rw-heading">
    <span class="rw-ribbon" aria-hidden="true"></span>
    <p id="rw-heading" class="rw-title">
      {{ locale === 'en' ? 'International Awards' : 'Uluslararası Ödüller' }}
      <span class="rw-sub">HolidayCheck{{ ratingText ? ` ${ratingText}` : '' }} Award &amp; TripAdvisor Certificate of Excellence</span>
    </p>
    <div class="light-bg">
      <ClientOnly>
        <div class="row rew-body justify-content-center" style="margin-inline: 1rem">
          <a
            v-for="(path, i) in rewards"
            :key="i"
            :href="linkForReward(path, i)"
            target="_blank"
            rel="noopener noreferrer nofollow"
            class="rw-link"
            :title="altForReward(path, i)"
          >
            <NuxtImg
              format="webp"
              :src="imgUrl(path)"
              :alt="altForReward(path, i)"
              :title="altForReward(path, i)"
              class="img"
              loading="lazy"
              placeholder
              width="150"
              height="150"
            />
          </a>
        </div>
      </ClientOnly>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import { HOTEL } from '~/utils/schema/hotel.entity.js'

const { locale } = useI18n()
const store = generalStore()
const rewards = computed(() => store.getRewardsImages || [])

const TRIPADVISOR_URL = 'https://www.tripadvisor.com.tr/Hotel_Review-g12934547-d651191-Reviews-Salamis_Bay_Conti_Resort_Hotel_Casino-Yeni_Bogazici_Famagusta_District.html'
const HOLIDAYCHECK_URL = 'https://www.holidaycheck.de/hi/salamis-bay-conti-resort-hotel-casino/0787cbc0-a97f-3532-956e-725a77d2055b'

// HolidayCheck figure from utils/schema/hotel.entity.js, shown as visible,
// attributed text only — it is intentionally not emitted as JSON-LD
// aggregateRating (see the comment on HOTEL.rating.aggregate).
const ratingText = computed(() => {
  const agg = HOTEL.rating.aggregate
  if (!agg) return null
  const numLocale = locale.value === 'en' ? 'en-US' : 'tr-TR'
  const rating = Number(agg.ratingValue).toLocaleString(numLocale)
  const count = Number(agg.reviewCount).toLocaleString(numLocale)
  const suffix = locale.value === 'en' ? 'reviews' : 'yorum'
  return `${rating}/${agg.bestRating} (${count} ${suffix})`
})

function imgUrl(path) {
  return `https://api2.salamisbayconti.com/storage/${path}`
}

function isTripadvisor(path, i) {
  const name = path.split('/').pop().toLowerCase()
  return i === 0 || name.includes('tripadvisor') || name.includes('trip')
}

function altForReward(path, i) {
  const name   = path.split('/').pop().toLowerCase()
  const year   = (name.match(/\d{4}/) || [])[0] ?? ''
  const suffix = ' – Salamis Bay Conti Resort Hotel'

  if (isTripadvisor(path, i)) return `TripAdvisor Certificate of Excellence ${year}${suffix}`
  if (name.includes('gold'))   return `HolidayCheck Gold Award ${year}${suffix}`
  return `HolidayCheck Award ${year}${suffix}`
}

function linkForReward(path, i) {
  return isTripadvisor(path, i) ? TRIPADVISOR_URL : HOLIDAYCHECK_URL
}

// Schema.org — awards merged into the existing #organization node via shared @id.
// No FAQPage here: this section only shows a heading + logo badges, never
// visible Q&A text, so an FAQPage would describe content the page doesn't
// actually show — see the FAQ schema audit for why that was removed.
useHead({
  script: [
    {
      type: 'application/ld+json',
      key: 'awards-schema',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Organization',
        '@id': 'https://salamisbayconti.com/#organization',
        name: 'Salamis Bay Conti Resort Hotel',
        url: 'https://salamisbayconti.com',
        description: 'Award-winning 5-star resort hotel in Famagusta, North Cyprus. Winner of the HolidayCheck Award nine consecutive years (2017–2025), HolidayCheck Gold Award 2026, and TripAdvisor Certificate of Excellence 2016.',
        areaServed: {
          '@type': 'Place',
          name: 'Cyprus',
          alternateName: ['North Cyprus', 'Kıbrıs', 'Kuzey Kıbrıs', 'Famagusta', 'Gazimağusa']
        },
        award: [
          'TripAdvisor Certificate of Excellence 2016',
          'HolidayCheck Award 2017',
          'HolidayCheck Award 2018',
          'HolidayCheck Award 2020',
          'HolidayCheck Award 2021',
          'HolidayCheck Award 2022',
          'HolidayCheck Award 2023',
          'HolidayCheck Award 2024',
          'HolidayCheck Award 2025',
          'HolidayCheck Gold Award 2026'
        ]
      })
    }
  ]
})
</script>

<style scoped>
.rw-section {
  background: #F0E8D8;
  position: relative;
  overflow: hidden;
}

/* ─── Corner ribbon ──────────────────────────────────────── */
.rw-ribbon {
  position: absolute;
  top: 14px;
  right: -30px;
  width: 100px;
  height: 12px;
  background: linear-gradient(135deg, #0a3a52, #042333);
  box-shadow: 0 1px 4px rgba(4, 35, 51, 0.25);
  transform: rotate(45deg);
  pointer-events: none;
  z-index: 1;
}

/* ─── Title ──────────────────────────────────────────────── */
.rw-title {
  text-align: center;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(13px, 1.5vw, 15px);
  font-weight: 700;
  letter-spacing: .12em;
  text-transform: uppercase;
  color: #042333;
  margin: 0;
  padding-block: 0.5rem;
  line-height: 1;
}
.rw-sub {
  display: block;
  font-size: clamp(10px, 1vw, 11px);
  letter-spacing: .08em;
  font-weight: 700;
  color: #042333;
  margin-top: 4px;
  text-transform: none;
}
/* ─── Badge row ──────────────────────────────────────────── */
.rew-body {
  padding: 6px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 5px;
}

.rw-link {
  display: contents;
}

.img {
  width: 18%;
  height: auto;
  max-width: 85px;
  padding: 2px;
  object-fit: contain;
  filter: brightness(0) saturate(100%) invert(12%) sepia(100%) saturate(500%) hue-rotate(185deg);
  transition: opacity 200ms, filter 200ms;
}
.img:hover {
  opacity: .75;
}

@media (max-width: 768px) {
  .rew-body {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    justify-items: center;
  }
  .img { width: auto; max-width: 68px; height: auto; min-width: 0; }
}

@media (min-width: 2200px) {
  .rew-body { max-height: 120px; padding-block: 15px; }
  .img { margin: 0; }
}
</style>
